import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface User {
  user_id: string;
  username: string;
  /** Null for a support VISITOR (ADR-0081 D6): accountless by design, so no mailbox
   *  in this instance. Render it optionally — never substitute a placeholder. */
  email: string | null;
  full_name?: string | null;
  groups: string[];
}

interface AuthState {
  token: string | null;
  /** Opaque rotated refresh token (`trt_…`) — the LONG session. The
   *  access `token` above is short-lived; `lib/session.ts` swaps both
   *  via /auth/refresh before expiry. Null for sessions opened through
   *  flows that don't mint one (then no auto-renewal happens). */
  refreshToken: string | null;
  user: User | null;
  setSession: (token: string, user: User, refreshToken?: string | null) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      // `refreshToken` undefined = keep the current one (the rotation
      // path replaces it explicitly; legacy callers don't clear it).
      setSession: (token, user, refreshToken) =>
        set((s) => ({
          token,
          user,
          refreshToken: refreshToken === undefined ? s.refreshToken : refreshToken,
        })),
      logout: () => set({ token: null, refreshToken: null, user: null }),
      isAuthenticated: () => get().token !== null && get().user !== null,
      isAdmin: () => get().user?.groups.includes("admin") ?? false,
    }),
    { name: "hub-auth" },
  ),
);
